import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, DatePicker, Flex, Modal, Popconfirm, Select, Space, Table, TableColumnsType, message } from "antd";
import { CloudUploadOutlined, ReloadOutlined, UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import dayjs from "dayjs";

import StringUtils from "../../../../../../util/StringUtils";
import FileUtils from "../../../../../../util/FileUtils";
import ByteUtils from "../../../../../../util/ByteUtils";
import SessionService from "../../../../../../services/session/SessionService";
import Api from "../../../../../../api/Api";
import { FilterBar, FilterItem, FilterItemLabel, OfflineMetricsDataFileManagementWrapper, PreviewWrapper, TableWrapper } from "./OfflineMetricsDataFile.styled";
import OfflineMetricsDataFileApi from "../../../../../../api/OfflineMetricsDataFIleApi";
import PermissionWrapper from "../../../../../../components/permission/PermissionWrapper";

interface OfflineDeviceMetricsDataFileProps {
    deviceView: any
}

let OfflineMetricsDataFile: React.FC<OfflineDeviceMetricsDataFileProps> = (props)=> {
    // params
    let deviceView = props.deviceView;

    // api
    let offlineMetricsDataFileApi = new OfflineMetricsDataFileApi();
    let navigate = useNavigate();
    let [messageApi, contextHolder] = message.useMessage();

    // state
    let [loading, setLoading] = useState(true);
    let [offlineMetricsDataFileViews, setOfflineMetricsDataFileViews] = useState([]);
    let [startTime, setStartTime] = useState<dayjs.Dayjs>();
    let [endTime, setEndTime] = useState<dayjs.Dayjs>();

    let [previewData, setPreviewData] = useState<any>(null);
    let [previewFiles, setPreviewFiles] = useState<any>(null);
    let [uploading, setUploading] = useState(false);

    // functions
    let loadOfflineMetricsDataFileViews = async ()=> {
        setLoading(true);
        let offlineMetricsDataFileViews = (await offlineMetricsDataFileApi.queryViews({
            deviceId: deviceView.device.id,
            startTime: startTime?.toDate().getTime(),
            endTime: endTime?.toDate().getTime(),
        })).data.data;
        for(let offlineMetricsDataFileView of offlineMetricsDataFileViews) {
            offlineMetricsDataFileView.children=offlineMetricsDataFileView.offlineDeviceMetricsDataFileViews;
        }
        setOfflineMetricsDataFileViews(offlineMetricsDataFileViews);
        setLoading(false);
    };

    let formatFileName = (fileName: string)=> {
        if (fileName.lastIndexOf('/') >= 0) {
            return fileName.substring(fileName.lastIndexOf('/') + 1);
        }
        return fileName;
    }

    let formatTimestamp = (timestampUs)=> {
        if (timestampUs == null) {
            return '';
        }
        return moment(parseInt(timestampUs)).format('YYYY-MM-DD HH:mm:ss');
    };

    let getDownloadDataFileUrl = (dataFileView)=> {
        let downloadUrl = Api.URL_PREFIX + `/offline-metrics-data-file/download?id=${dataFileView.offlineDeviceMetricsDataFile.id}&authorization=${SessionService.getInstance().getAuthoriazation()}`;
        return downloadUrl;
    };

    let onDeleteDataFile = async (dataFileView)=> {
        await offlineMetricsDataFileApi.delete(dataFileView.offlineDeviceMetricsDataFile.id);
        messageApi.success('删除成功:' + dataFileView.offlineDeviceMetricsDataFile.name);
        loadOfflineMetricsDataFileViews();
    }

    let onDateTimeRangeChange = (dateTimeRangeValue: any[])=> {
        setStartTime(dateTimeRangeValue[0]);
        setEndTime(dateTimeRangeValue[1]);
    };


    let onUploadDataFile = ()=> {
        let fileSelect=document.createElement('input');
        fileSelect.type='file';
        fileSelect.multiple=true;
        fileSelect.style.display = 'none';
        fileSelect.addEventListener('click', (event)=>{
            event.stopPropagation();
        });

        let onSelectFile = async () => {
            setPreviewFiles(fileSelect.files);
            try {
                let file = fileSelect.files[0];
                let previewData = (await offlineMetricsDataFileApi.preview(deviceView.device.id, file)).data.data;
                previewData = previewData.length > 100 ? previewData.slice(0, 100) : previewData;
                setPreviewData(previewData); 
            } catch (e) {
                messageApi.error('文件预览失败:' + e);
            }
        };
        fileSelect.addEventListener('change', (event)=>{
            event.stopPropagation();
            onSelectFile();
        });

        fileSelect.click();
    };

    let uploadPreviewFiles = async ()=> {
        setUploading(true);
        for (let i = 0; i<previewFiles.length; i++) {
            try {
                let file = previewFiles[0];
                let previewData = (await offlineMetricsDataFileApi.upload(deviceView.device.id, file)).data.data;
                setPreviewData(previewData); 
                messageApi.success('文件导入成功:' + file.name);
                loadOfflineMetricsDataFileViews();
            } catch (e) {
                messageApi.error('文件导入失败:' + e);
            }
        }
        setUploading(false);
    }

    // effect
    useEffect(()=> {
        loadOfflineMetricsDataFileViews();
    }, [startTime, endTime]);

    // preview data table
    let previewDataColumnsTotalWidth = 0;
    let previewDataColumns: any[] = [];
    if (previewData != null) {
        let deviceTemplateConfiguration = JSON.parse(deviceView.deviceTemplate.configuration);
        previewDataColumns.push({
            title: '时间',
            dataIndex: 'ts',
            key: 'ts',
            width: 200,
            align: 'left',
            fixed: 'left',
            render: (text, record, index)=> {
                return formatTimestamp(record.ts);
            }
        });
        previewDataColumnsTotalWidth += 200;
        for (let property of deviceTemplateConfiguration.properties) {
            let column = {
                title: property.name + '（' + StringUtils.emptyToDefault(property.unit, '-') + '）',
                dataIndex: property.key,
                key: property.key,
                width: 150,
                align: 'center',
                render: (text, record, index)=> {
                    return record[property.key];
                },
            }
            previewDataColumns.push(column);
            previewDataColumnsTotalWidth += 150;
        }
    }

    // render
    const dataFileColumns: TableColumnsType<any> = [
        {
            title: '日期/文件名',
            dataIndex: 'name',
            key: 'name',
            width: '200px',
            render: (text, record, index)=> {
                return <div style={{whiteSpace: 'nowrap', textAlign: 'left'}}>{formatFileName(record.offlineDeviceMetricsDataFile.name)}</div>
            },
        },
        {
            title: '起始时间',
            dataIndex: 'startTime',
            key: 'startTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatTimestamp(record.offlineDeviceMetricsDataFile.startTimestamp)}</span>
            }
        },
        {
            title: '结束时间',
            dataIndex: 'endTime',
            key: 'endTime',
            align: 'center',
            render: (text, record, index)=> {
                return <span>{formatTimestamp(record.offlineDeviceMetricsDataFile.endTimestamp)}</span>
            }
        },
        {
            title: '文件大小',
            dataIndex: 'size',
            key: 'size',
            align: 'center',
            render: (text, record, index)=> {
                let size = Number(record.offlineDeviceMetricsDataFile.size);
                if (size == null) {
                    return StringUtils.emptyToSlash('');
                }
                return ByteUtils.formatBytes(size);
            }
        },
        {
            title: '操作',
            key: 'action',
            align: 'center',
            render: (text, record, index)=> {
                return record.offlineDeviceMetricsDataFile.id && <Space size="middle">
                    {record.offlineDeviceMetricsDataFile.path && <a href={getDownloadDataFileUrl(record)}>下载</a>}
                    {record.offlineDeviceMetricsDataFile.path && <PermissionWrapper permissionCodePath="device-management.device.delete-device">
                        <Popconfirm
                                title="删除文件"
                                description={ <div style={{paddingTop: "10px", paddingBottom: '10px'}}>此操作将删除相关数据，删除的数据无法恢复，确认要删除么?</div> }
                                onConfirm={()=> onDeleteDataFile(record)}
                                okText="确定"
                                cancelText="取消"
                            > 
                            <a>删除文件</a>
                        </Popconfirm>
                    </PermissionWrapper> }
                </Space>
            }
        }
    ];

    return <OfflineMetricsDataFileManagementWrapper>
            {contextHolder}
            <FilterBar>
                <Space size="middle">
                    <FilterItem>
                        <FilterItemLabel>时间范围</FilterItemLabel>
                        <DatePicker.RangePicker
                            style={{width: '330px'}}
                            showTime={{ format: 'HH:mm:ss' }}
                            format="YYYY-MM-DD HH:mm:ss"
                            defaultValue={[dayjs().add(-1, 'd'), dayjs()]}
                            value={[startTime, endTime]}
                            allowEmpty={[false, true]}
                            onOk={onDateTimeRangeChange}
                        />
                    </FilterItem>
                </Space>


                <Space size="middle">
                    <Button type="primary" onClick={onUploadDataFile} icon={<CloudUploadOutlined />}>上传数据文件</Button>
                </Space>
            </FilterBar>
            <TableWrapper>
                <Table 
                    style={{height: '100%'}}
                    rowKey={(record)=> record.offlineDeviceMetricsDataFile.name}
                    columns={dataFileColumns} 
                    dataSource={offlineMetricsDataFileViews}
                    loading={loading}
                    pagination={false}
                    scroll={{y: 'calc(100vh - 350px)'}}
                />

                <Modal open={previewData != null} 
                    onClose={()=> setPreviewData(null)} 
                    onOk={()=> uploadPreviewFiles()}
                    closable={false}
                    destroyOnClose={true}
                    width={1000}
                    title="数据解析预览"
                    footer={[
                        <Flex style={{gap: '20px', justifyContent: 'flex-end'}} key="footer-button-wrapper" >
                            <Button type="primary" disabled={uploading} loading={uploading} onClick={()=> uploadPreviewFiles()}>导入</Button>
                            <Button type="primary" danger onClick={()=> setPreviewData(null)}>取消</Button>
                        </Flex>
                    ]}
                    >
                    <PreviewWrapper>
                        <Table 
                            style={{height: '100%'}}
                            rowKey={(record, index)=> index}
                            columns={previewDataColumns} 
                            dataSource={previewData}
                            loading={loading}
                            pagination={false}
                            scroll={{x: previewDataColumnsTotalWidth}}
                            sticky={true}
                        />
                    </PreviewWrapper>
                </Modal>
            </TableWrapper>
    </OfflineMetricsDataFileManagementWrapper>
};

export default OfflineMetricsDataFile;
